'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertCircle, FileCode, Layers } from 'lucide-react'

interface DiscoveryModalProps {
  open: boolean
  onClose: () => void
  onSubmit: () => Promise<void>
  repositoryName: string
  discoveryStatus?: string
}

export function DiscoveryModal({
  open,
  onClose,
  onSubmit,
  repositoryName,
  discoveryStatus,
}: DiscoveryModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    try {
      await onSubmit()
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start discovery')
    } finally {
      setLoading(false)
    }
  }

  const isRunning = discoveryStatus === 'pending' || discoveryStatus === 'running'

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Generate Network & Invariants</DialogTitle>
          <DialogDescription>
            Automatically discover network structure and generate invariants for {repositoryName}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          <div className="bg-muted p-4 rounded-lg space-y-3">
            <div className="flex items-start gap-3">
              <Layers className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-medium">.etherpilot/network.yml</h4>
                <p className="text-sm text-muted-foreground">
                  Network topology and device configuration
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileCode className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-medium">.etherpilot/invariants.yml</h4>
                <p className="text-sm text-muted-foreground">
                  Network invariants and validation checks
                </p>
              </div>
            </div>
          </div>

          {isRunning && (
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <span>
                Discovery is already {discoveryStatus}. Please wait for it to complete.
              </span>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            This process will analyze your network configurations and automatically generate:
          </p>
          <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 ml-2">
            <li>Device roles and tiers (spine, leaf, border, etc.)</li>
            <li>Physical and logical network links</li>
            <li>BGP sessions and routing topology</li>
            <li>Network pattern detection (spine-leaf, hub-spoke, etc.)</li>
            <li>Recommended validation checks and invariants</li>
          </ul>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || isRunning}
          >
            {loading ? 'Starting Discovery...' : isRunning ? 'Discovery Running' : 'Start Discovery'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}